import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChallengeBattleShipService, DefaultConfBattleBoat, PlacedShip, ShotPlaceholder, TPlayer } from 'src/app/gamification/command-center/services/challenge-battle-ship.service';

@Component({
  selector: 'cv-board-ships',
  templateUrl: './board-ships.component.html',
  styleUrl: './board-ships.component.scss'
})
export class BoardShipsComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input()
  canShot = false;
  cellMarked = false;
  displayTooltip: boolean = false;

  @ViewChild('BoardShipContainer') boardShipContainer : ElementRef;
  private resizeObserver: ResizeObserver;
  private subscriptions = new Subscription();

  @Input()
  challenge = null;

  placedShips: PlacedShip[] = [];
  sunkenShips: PlacedShip[] = [];
  mySunkenShips: PlacedShip[] = [];
  player: TPlayer = null;
  board = [];
  shots: ShotPlaceholder[][] = [];
  rivalsShots: ShotPlaceholder[][] = [];
  cellSize: number = DefaultConfBattleBoat.ShootCellSize;

  constructor(private challengeBattleBoatService: ChallengeBattleShipService) {
    this.challengeBattleBoatService.setInitialData();
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(entries => {
      for(let entry of entries){
        if(entry.contentRect) {
          const w = entry.contentRect.width || 0;
          const currentSize = Math.max(Math.round((w - 10) / 8), 23);
          this.cellSize = Math.min(currentSize, 28); // min size = 23, max size = 28
        }
      }
    });
    if(this.boardShipContainer && this.boardShipContainer.nativeElement){
      this.resizeObserver.observe(this.boardShipContainer.nativeElement);
    }
  }

  ngOnInit(): void {
    this.subscriptions.add(this.challengeBattleBoatService.myShips$.subscribe(placedShips => {
      this.placedShips = placedShips;
    }));
    this.subscriptions.add(this.challengeBattleBoatService.board$.subscribe(board => {
      this.board = board;
    }));
    this.subscriptions.add(this.challengeBattleBoatService.shots$.subscribe(shots => {
      this.shots = shots;
      const allShoots =  shots.flatMap(sd => sd.filter(s => s.shot));
    }));

    this.subscriptions.add(this.challengeBattleBoatService.sunkenShips$.subscribe(sunkenShips => {
      this.sunkenShips = sunkenShips;
    }));
    this.subscriptions.add(this.challengeBattleBoatService.player$.subscribe(player => {
      this.player = player;
    }));
    this.subscriptions.add(this.challengeBattleBoatService.rivalsShots$.subscribe(rivalsShots => {
      this.rivalsShots = rivalsShots;
    }));
    this.subscriptions.add(this.challengeBattleBoatService.mySunkenShips$.subscribe(mySunkenShips => {
      this.mySunkenShips = mySunkenShips;
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.challenge) {
      if (this.challenge && this.challenge?.ChallengeID) this.challengeBattleBoatService.getAllShipsWithShots(this.challenge.ChallengeID);
    } else {
      this.challengeBattleBoatService.setInitialData();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.challengeBattleBoatService.setInitialData();
    if(this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getShipImageUrl(ship: { id: string, size: number, orientation: string }) {
    return `../App_Themes/clearview-default/images/challenges/battleboat/${ship.id.toLowerCase()}.svg`;
  }

  shotMarkCell(){
    let col = -1, row = -1;
    this.shots.forEach((s, i) => {
      s.forEach((sd, j) => {
        if(sd.mark){
          col = j;
          row = i;
        }
      });
    });
    if(row < 0 || col < 0) return;
    const shipData = {
      _column: col,
      _row: row,
      challengeId: this.challenge.ChallengeID
    };
    this.challengeBattleBoatService.createShot(shipData);
    this.cellMarked = false;
  }

  onCellClick(row: number, col: number): void {
    if(!this.canShot) return;
    if(this.shots[row] && this.shots[row][col] && this.shots[row][col].shot) return;
    this.challengeBattleBoatService.markCellSelected(row, col, true);
    this.cellMarked = true;
  }

  getPlacedShipStyle(ship: PlacedShip): Object {
    const size = ship.size * this.cellSize;
    const position = this.challengeBattleBoatService.getCellPositions(ship.position.x, ship.position.y, this.cellSize, 0);
    return {
      'left.px': position.positionX,
      'top.px': position.positionY,
      'width.px': size,
      'height.px': this.cellSize - 1,
      'transform': ship.orientation === 'vertical' ? `rotate(90deg) translate(0px, -${this.cellSize}px)` : '',
      'transform-origin': ship.orientation === 'vertical' ? '0 0' : ""
    };
  }

  getMyRanking(): number | string {
    if (!this.challenge || !this.challenge.Rankings || this.challenge.Rankings.length == 0 || !this.player?.LoginID) return '---';
    const myRanking = this.challenge.Rankings.find(r => r.LoginID === this.player.LoginID);
    return myRanking?.ViewScore ?? "---";
  }
  
  getRivalRanking(): number | string {
    if (!this.challenge || !this.challenge.Rankings || this.challenge.Rankings.length == 0 || !this.player?.LoginID) return '---';
    const rivalRanking = this.challenge.Rankings.find(r => r.LoginID !== this.player.LoginID);
    return rivalRanking?.ViewScore ?? "---";
  }

  isCellInSunkenShip(row: number, col: number, sunkenShips: PlacedShip[]): boolean {
    return sunkenShips.some(ship => {
      const startX = ship.position.x;
      const startY = ship.position.y;
      const endX = ship.orientation === 'horizontal' ? startX + ship.size - 1 : startX;
      const endY = ship.orientation === 'vertical' ? startY + ship.size - 1 : startY;
  
      return row >= startY && row <= endY && col >= startX && col <= endX;
    });
  }
  
  toggleTooltip(event: boolean): void {
    this.displayTooltip = event;
  }

}
